import { useCallback, useEffect, useState } from "react";
import API, { formatApiError } from "../../lib/api";
import { Button } from "../ui/button";
import { Switch } from "../ui/switch";
import { toast } from "sonner";
import { CheckCircle, Circle, BellRinging } from "@phosphor-icons/react";
import { fmtDateTime } from "../../lib/dateUtil";

/** Read receipts for an approved document: switch acknowledgement on, see who has read it, nudge the rest. */
export default function HseAckCard({ doc, onChanged }) {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => API.get(`/hse/documents/${doc.id}/acknowledgements`).then(({ data }) => setD(data)).catch(() => {}), [doc.id]);
  useEffect(() => { load(); }, [load, doc.requires_ack]);
  if (!d) return null;

  const toggle = async (enabled) => {
    setBusy(true);
    try {
      const { data } = await API.put(`/hse/documents/${doc.id}/acknowledgement`, { enabled });
      toast.success(enabled ? `Read receipts on — ${data.notified} operative${data.notified === 1 ? "" : "s"} asked to read it` : "Read receipts off");
      load(); onChanged?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  const remind = async () => {
    try { const { data } = await API.post(`/hse/documents/${doc.id}/acknowledgements/remind`); toast.success(`Reminder sent to ${data.reminded} operative${data.reminded === 1 ? "" : "s"}`); } catch (e) { toast.error(formatApiError(e)); }
  };
  const canToggle = doc.status === "approved";

  return (
    <div className="bg-card border border-border rounded-lg p-4 text-xs space-y-3" data-testid="hse-ack-card">
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">Read receipts</p>
          <p className="text-muted-foreground mt-0.5">{canToggle ? "Every active in-house operative must read this version and sign it in My Work." : "Approve the document to ask operatives to read it."}</p>
        </div>
        <Switch checked={!!d.requires_ack} disabled={!canToggle || busy} onCheckedChange={toggle} data-testid="hse-ack-toggle" />
      </div>
      {d.requires_ack && (
        <>
          <div className="flex items-center gap-2" data-testid="hse-ack-progress">
            <div className="flex-1 h-2 rounded-full bg-muted overflow-hidden"><div className={`h-full transition-all ${d.pct === 100 ? "bg-emerald-500" : "bg-primary"}`} style={{ width: `${d.pct}%` }} /></div>
            <span className="font-semibold tabular-nums">{d.acknowledged}/{d.total} read</span>
          </div>
          {d.total === 0 && <p className="text-muted-foreground">No active in-house operatives yet — they will be asked as soon as they are added.</p>}
          <ul className="space-y-1 max-h-56 overflow-y-auto" data-testid="hse-ack-list">
            {d.operatives.map(o => (
              <li key={o.user_id} className="flex items-center gap-2" data-testid={`hse-ack-row-${o.user_id}`}>
                {o.acknowledged_at ? <CheckCircle size={14} weight="fill" className="text-emerald-600 shrink-0" /> : <Circle size={14} className="text-amber-500 shrink-0" />}
                <span className={`flex-1 min-w-0 ${o.left ? "line-through text-muted-foreground" : ""}`} title={o.name}>{o.name}</span>
                <span className="text-muted-foreground whitespace-nowrap text-right">{o.acknowledged_at ? fmtDateTime(o.acknowledged_at) : "not yet"}{d.quiz_enabled && o.attempts > 0 ? <span className="block text-[11px] text-foreground/70" title="Understanding check attempts">{o.attempts} {o.attempts === 1 ? "try" : "tries"}{o.passed ? " · passed" : ""}</span> : null}</span>
              </li>
            ))}
          </ul>
          {d.outstanding > 0 && <Button size="sm" variant="outline" className="w-full h-8" onClick={remind} data-testid="hse-ack-remind-btn"><BellRinging size={13} className="mr-1" /> Remind {d.outstanding} outstanding</Button>}
        </>
      )}
    </div>
  );
}
